import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { SolicitudResponse } from "../../../shared/response/proyectos/ProyectoResponse";
import { SolicitudPayload } from "../../../shared/services/api/payload/proyectos/SolicitudPayload";
import { SolicitudSaveService } from "../../services/proyectos/SolicitudSaveService";

/**
 * Solicitud de cantidad de un ítem (solo Nitrofert; reemplaza al modal de gasto). Pide solo la cantidad, en la
 * unidad del ítem, y una observación opcional. Nitrofert la atiende registrando los gastos (precio, proveedor,
 * referencia) por su API, en una o varias entregas.
 */
export class SolicitudModal extends BaseSmallModalFormComponent<SolicitudResponse, SolicitudPayload, SolicitudSaveService> {
  private _cantidad!: HTMLInputElement;
  private _obs!: HTMLTextAreaElement;

  /**
   * @param _proyectoId Proyecto.
   * @param _itemId Id del CatalogoItem (como en los gastos), no el del ProyectoItem.
   * @param _itemNombre Nombre del ítem, para el encabezado.
   * @param _unidad Unidad de la cantidad (`cantUnidad` del ítem).
   * @param _onSolicitada Se llama con la solicitud creada (la pantalla recarga el detalle).
   */
  constructor(
    private readonly _proyectoId: string,
    private readonly _itemId: string,
    private readonly _itemNombre: string,
    private readonly _unidad: string,
    private readonly _onSolicitada: (s: SolicitudResponse) => void,
  ) {
    super(new SolicitudSaveService());
  }

  protected get title(): string { return "Solicitar cantidad"; }
  protected get saveText(): string { return "Solicitar"; }

  /** Ítem, cantidad (con su unidad) y observaciones. */
  protected renderBody(): string {
    return `
      <div class="cl-form-fields">
        <div class="cl-form-group">
          <label class="cl-form-label">${this._esc(this._itemNombre)}</label>
          <div class="pr-form-note" style="margin-bottom:6px">Nitrofert atiende la solicitud y registra los gastos (precio, proveedor y referencia), en una o varias entregas.</div>
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="so-cant">Cantidad${this._unidad ? ` (${this._esc(this._unidad)})` : ""} <span class="cl-required">*</span></label>
          <input id="so-cant" class="sx-input sx-input--numeric" type="number" min="0" step="0.01" placeholder="0">
        </div>
        <div class="cl-form-group">
          <label class="cl-form-label" for="so-obs">Observaciones</label>
          <textarea id="so-obs" class="sx-input" rows="2" maxlength="1000" style="resize:vertical"></textarea>
        </div>
      </div>
    `;
  }

  /** Habilita "Solicitar" con una cantidad mayor que cero. */
  protected override onAfterRender(el: HTMLDivElement): void {
    this._cantidad = el.querySelector<HTMLInputElement>("#so-cant")!;
    this._obs = el.querySelector<HTMLTextAreaElement>("#so-obs")!;
    this._cantidad.addEventListener("input", () => this.setSaveDisabled(!(Number(this._cantidad.value) > 0)));
    this._cantidad.focus();
  }

  /** Siempre crea: las solicitudes no se editan. */
  protected buildPayload(): SolicitudPayload {
    return {
      id: null,
      proyectoId: this._proyectoId,
      itemId: this._itemId,
      cantidad: Number(this._cantidad.value),
      observaciones: this._obs.value.trim() || null,
    };
  }

  /** Avisa a la pantalla y cierra. */
  protected override onSaved(item: SolicitudResponse): void {
    this._onSolicitada(item);
    this.dispose();
  }

  /** Escapa texto para insertarlo en HTML. */
  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }
}
